import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, ArrowRight } from "lucide-react";
import { COMPANY, NAV_LINKS } from "../data";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={
        "fixed top-0 inset-x-0 z-50 bg-white border-b transition-all duration-300 " +
        (scrolled ? "border-zinc-200 shadow-sm" : "border-transparent")
      }
    >
      {/* Top strip */}
      <div className="hidden md:block bg-brand-green text-white text-[11px] font-mono tracking-widest uppercase">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-2 flex items-center justify-between">
          <span data-testid="header-tagline">Wind Energy Transportation • Project Logistics • Since 2007</span>
          <div className="flex items-center gap-6">
            {COMPANY.phones.map((p) => (
              <a
                key={p.raw}
                href={`tel:${p.raw}`}
                className="flex items-center gap-2 hover:text-brand-orange transition-colors"
                data-testid={`header-phone-${p.raw}`}
              >
                <Phone className="w-3 h-3" />
                {p.value}
              </a>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-3 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="brand-logo-link">
          <img
            src={COMPANY.logoUrl}
            alt="MRL Transports"
            className="h-20 md:h-24 lg:h-28 w-auto"
            data-testid="brand-logo-img"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
          {NAV_LINKS.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-link-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                "px-4 py-2 font-heading text-[13px] uppercase tracking-widest transition-colors " +
                (isActive
                  ? "text-brand-green border-b-2 border-brand-orange"
                  : "text-brand-ink hover:text-brand-green")
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:block">
          <Link
            to="/contact"
            data-testid="header-cta-quote"
            className="inline-flex items-center gap-2 bg-brand-orange text-white font-heading uppercase text-xs px-6 py-3 border-2 border-brand-orange hover:bg-brand-green hover:border-brand-green transition-colors whitespace-nowrap"
            style={{ letterSpacing: "0.08em" }}
          >
            <span>Get</span>
            <span>a</span>
            <span>Quote</span>
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <button
          className="lg:hidden p-2"
          onClick={() => setOpen((o) => !o)}
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X className="w-6 h-6 text-brand-green" /> : <Menu className="w-6 h-6 text-brand-green" />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden border-t border-zinc-200 bg-white" data-testid="mobile-menu">
          <div className="px-6 py-4 flex flex-col">
            {NAV_LINKS.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`mobile-nav-link-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  "py-3 border-b border-zinc-100 font-heading uppercase tracking-widest text-sm " +
                  (isActive ? "text-brand-orange" : "text-brand-ink")
                }
              >
                {n.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="mobile-cta-quote"
              className="mt-4 inline-flex items-center justify-center gap-2 bg-brand-orange text-white font-heading uppercase text-xs px-6 py-3"
              style={{ letterSpacing: "0.08em" }}
            >
              <span>Get</span><span>a</span><span>Quote</span>
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
